/**
 * reorderIntoCart.js
 * ----------------------------------------------------------------------------
 * "Order again in one tap" (2 Nov 2026). The ONE place that puts a rebuilt
 * order (GET /api/orders/:orderId/reorder) into the cart it belongs to.
 *
 * The server decides everything a line carries: today's price, the options
 * and choices that still exist, the vendor and the branch. Each line goes in
 * through utils/vendorCartLines.js, the mapping the /shop grid's Add to cart
 * uses too, so a reordered line is stored exactly as a line added by hand:
 *
 *   restaurant  restaurantCart, keyed by `vendorOwnerId`
 *   pharmacy    healthcareCart, keyed by `vendorOwnerId`
 *   brand       brandCart, keyed by `vendorDocumentId` (the brand)
 *   shop        retailerWholesalerCart, keyed by `vendorDocumentId` (the shop)
 *   lusuku      the in-house cart (one list, no vendor key)
 *
 * The keys are the identities the server names (vendorOwnerId, the owner's
 * account; vendorDocumentId, the vendor's own record), never worked out
 * here; `vendorId` is read only when a response predates them. The website
 * has one shop cart, so `onWholesaleList` changes nothing here.
 *
 * The vendor's cart is REPLACED, not added to: the order comes back as it
 * was. When that cart already holds items, the button asks first
 * (cartLinesReplacedBy counts them), so nothing a shopper was building is
 * lost without a yes. Other vendors' carts are left alone. The lines carry no branch confirmation, so each
 * cart's own branch check settles them before Place order, as it does for a
 * cart read back from storage.
 * ----------------------------------------------------------------------------
 */
import { removeItemFromCart as removeFromLusukuCart } from "../redux/cartRedux";
import { clearCart as clearRestaurantCart, switchRestaurant } from "../redux/restaurantCartSlice";
import { clearCart as clearPharmacyCart, switchHealthcareVendor } from "../redux/healthcareCartSlice";
import { clearCart as clearBrandCart, switchBrand } from "../redux/brandCartSlice";
import {
  clearCart as clearShopCart,
  setCurrentVendor as setCurrentShop,
} from "../redux/retailerWholesalerCartSlice";
import { shopPath } from "./seoPaths";
import { CART_VERTICALS, hasVendorCart, vendorCartKey, vendorLineAddActions } from "./vendorCartLines";

/* The verticals and cart keys are the shared ones (utils/vendorCartLines.js),
   which the /shop grid's Add to cart uses too. */
export const REORDER_VERTICALS = CART_VERTICALS;

const linesOf = (reorder) => (Array.isArray(reorder?.lines) ? reorder.lines.filter(Boolean) : []);

/** The key this vertical's website cart is stored under, or null (Lusuku has none). */
export const reorderCartKey = vendorCartKey;

/* Each line goes in exactly as the shared mapping adds it. */
const addLines = (vertical, lines, cartKey, outletId = null) =>
  lines.flatMap((line) => vendorLineAddActions(vertical, cartKey, line, { outletId }));

/* One builder per vertical: the actions that replace that vendor's cart,
   stored under `cartKey` (reorderCartKey's answer). */
const CART_ACTIONS = {
  [REORDER_VERTICALS.RESTAURANT]: ({ outletId }, lines, _state, cartKey) => [
    clearRestaurantCart(cartKey),
    ...addLines(REORDER_VERTICALS.RESTAURANT, lines, cartKey, outletId),
    switchRestaurant(cartKey),
  ],
  [REORDER_VERTICALS.PHARMACY]: ({ outletId }, lines, _state, cartKey) => [
    clearPharmacyCart(cartKey),
    ...addLines(REORDER_VERTICALS.PHARMACY, lines, cartKey, outletId),
    switchHealthcareVendor(cartKey),
  ],
  [REORDER_VERTICALS.BRAND]: (_reorder, lines, _state, cartKey) => [
    clearBrandCart(cartKey),
    ...addLines(REORDER_VERTICALS.BRAND, lines, cartKey),
    switchBrand(cartKey),
  ],
  [REORDER_VERTICALS.SHOP]: (_reorder, lines, _state, cartKey) => [
    clearShopCart(cartKey),
    ...addLines(REORDER_VERTICALS.SHOP, lines, cartKey),
    setCurrentShop(cartKey),
  ],
  /* Lusuku is its one vendor, so replacing it means emptying the whole
     in-house cart first. */
  [REORDER_VERTICALS.LUSUKU]: (_reorder, lines, state) => {
    const existingIds = [...new Set((state?.cart?.products || []).map((product) => product?._id).filter(Boolean))];
    return [
      ...existingIds.map((_id) => removeFromLusukuCart({ _id })),
      ...addLines(REORDER_VERTICALS.LUSUKU, lines, null),
    ];
  },
};

/** Does this vertical have a cart, and does the rebuilt order name its vendor? */
const isCartable = (reorder) =>
  Boolean(CART_ACTIONS[reorder?.vertical]) &&
  hasVendorCart(reorder?.vertical) &&
  (reorder.vertical === REORDER_VERTICALS.LUSUKU || Boolean(reorderCartKey(reorder)));

/**
 * The cart actions a rebuilt order becomes, in dispatch order. Pure: `state`
 * is the store's state (read for the in-house cart only). An order with no
 * line that can be bought today changes no cart at all.
 *
 * @param {object} reorder the endpoint's body
 * @param {object} [state]
 * @returns {object[]}
 */
export const reorderCartActions = (reorder, state = {}) => {
  const lines = linesOf(reorder).filter((line) => line.product);
  if (!lines.length || !isCartable(reorder)) return [];
  return CART_ACTIONS[reorder.vertical](reorder, lines, state, reorderCartKey(reorder));
};

/**
 * Where the shopper lands to review the cart: the storefront with its cart
 * open for the verticals whose cart lives on the storefront (restaurant,
 * pharmacy), the cart hub for the rest.
 *
 * @returns {{ pathname: string, search?: string, state?: object }}
 */
export const reorderCartLocation = (reorder) => {
  const storefront = { restaurant: "restaurant", pharmacy: "healthcare" }[reorder?.vertical];
  // The storefront address carries the owner, else the record (seoPaths).
  const pathname = storefront
    ? shopPath(storefront, {
        _id: reorder.vendorDocumentId || reorder.vendorId,
        owner: reorder.vendorOwnerId || null,
        name: reorder.vendorName || "",
      })
    : null;
  if (!pathname) return { pathname: "/cart" };
  return {
    pathname,
    search: reorder.outletId ? `?outletId=${encodeURIComponent(reorder.outletId)}` : "",
    state: { openCart: true },
  };
};

const NOTICE_NAMES = 3;

/**
 * The one short message the shopper reads after the tap: what went back in
 * the cart and, when something could not, which items and why.
 *
 * @returns {{ tone: "success" | "warning" | "error", text: string }}
 */
export const reorderNotice = (reorder) => {
  const added = linesOf(reorder).length;
  const unavailable = Array.isArray(reorder?.unavailable) ? reorder.unavailable.filter(Boolean) : [];
  const named = unavailable
    .slice(0, NOTICE_NAMES)
    .map(({ name, reason }) => (reason ? `${name} (${String(reason).toLowerCase()})` : name))
    .join(", ");
  const more = unavailable.length > NOTICE_NAMES ? ` and ${unavailable.length - NOTICE_NAMES} more` : "";
  const itemWord = added === 1 ? "item" : "items";
  if (!added) {
    return {
      tone: "error",
      text: unavailable.length
        ? `None of this order can be bought right now: ${named}${more}.`
        : "None of this order can be bought right now.",
    };
  }
  if (!unavailable.length) return { tone: "success", text: `${added} ${itemWord} back in your cart at today's prices.` };
  return { tone: "warning", text: `${added} ${itemWord} back in your cart. Not available: ${named}${more}.` };
};

const VENDOR_CART_SLICE = Object.freeze({
  [REORDER_VERTICALS.RESTAURANT]: "restaurantCart",
  [REORDER_VERTICALS.PHARMACY]: "healthcareCart",
  [REORDER_VERTICALS.BRAND]: "brandCart",
  [REORDER_VERTICALS.SHOP]: "retailerWholesalerCart",
});

/**
 * How many lines already in the cart a rebuilt order would replace: that
 * vendor's cart, or the whole in-house cart for Lusuku. 0 when the cart is
 * empty or the order changes no cart. Pure.
 *
 * @param {object} reorder the endpoint's body
 * @param {object} [state] the store's state
 * @returns {number}
 */
export const cartLinesReplacedBy = (reorder, state = {}) => {
  if (!reorderCartActions(reorder, state).length) return 0;
  if (reorder.vertical === REORDER_VERTICALS.LUSUKU) {
    return new Set((state?.cart?.products || []).map((product) => product?._id).filter(Boolean)).size;
  }
  const vendorCart = state?.[VENDOR_CART_SLICE[reorder.vertical]]?.carts?.[reorderCartKey(reorder)];
  return vendorCart && typeof vendorCart === "object" ? Object.keys(vendorCart).length : 0;
};

/** The question asked before a rebuilt order replaces items already in the cart. */
export const replaceCartQuestion = (reorder, count) => {
  const itemWord = count === 1 ? "item" : "items";
  const cartName = reorder?.vertical === REORDER_VERTICALS.LUSUKU ? "Lusuku" : reorder?.vendorName || "this vendor's";
  return `This replaces the ${count} ${itemWord} already in your ${cartName} cart. Continue?`;
};

/**
 * Put a rebuilt order into its cart (a thunk). Returns what the caller needs
 * next: how many lines went in and where to open the cart.
 */
export const reorderIntoCart = (reorder) => (dispatch, getState) => {
  const actions = reorderCartActions(reorder, typeof getState === "function" ? getState() : {});
  actions.forEach((action) => dispatch(action));
  return { added: actions.length ? linesOf(reorder).length : 0, location: reorderCartLocation(reorder) };
};

/** Can this order be rebuilt at all? Tickets and package deliveries cannot. */
export const isReorderable = (order) =>
  Boolean(order?._id) &&
  order.isPackageDelivery !== true &&
  order.orderKind !== "TICKET" &&
  Array.isArray(order.orderItems) &&
  order.orderItems.length > 0;

export default reorderIntoCart;
